import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import WhatsAppButton from "@/components/public/WhatsAppButton";
import { Link } from "wouter";
import { BRAND } from "@/brand";
import { useSeo } from "@/hooks/useSeo";

export default function PrivacyPage() {
  useSeo({ title: "Política de privacidad | Golf Motion Lab" });
  const lastUpdated = "29 de mayo de 2026";

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <PublicHeader />

      <section className="container py-20 md:py-32 max-w-3xl">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-4">
          Información legal
        </div>
        <h1
          className="font-display font-light text-[var(--forest-deep)] leading-[1.05] mb-6"
          style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}
        >
          Política de privacidad
        </h1>
        <p className="text-sm text-[var(--forest-deep)]/60 mb-12">
          Última actualización: {lastUpdated}
        </p>

        <div className="prose prose-neutral max-w-none text-[var(--forest-deep)]/85 leading-relaxed">
          <p>
            En <strong>{BRAND.name}</strong> nos tomamos muy en serio la privacidad y la
            seguridad de tus datos personales. La presente Política de Privacidad describe cómo
            recogemos, utilizamos, conservamos y protegemos la información personal que nos
            facilitas, en cumplimiento del Reglamento (UE) 2016/679 del Parlamento Europeo y del
            Consejo, de 27 de abril de 2016, relativo a la protección de las personas físicas en
            lo que respecta al tratamiento de datos personales (RGPD), de la Ley Orgánica 3/2018,
            de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos
            digitales (LOPDGDD), y de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad
            de la Información y de Comercio Electrónico (LSSI-CE).
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            1. Responsable del tratamiento
          </h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Responsable:</strong> Sergio Sánchez Marín
            </li>
            <li>
              <strong>NIF:</strong> 47398641B
            </li>
            <li>
              <strong>Domicilio:</strong> Calle Arquitecto Fernández nº 52, 4º A · 02005 Albacete (Albacete), España
            </li>
            <li>
              <strong>Nombre comercial:</strong> {BRAND.name}
            </li>
            <li>
              <strong>Correo electrónico:</strong>{" "}
              <a href={`mailto:${BRAND.email}`} className="text-[var(--forest)] underline">
                {BRAND.email}
              </a>
            </li>
            <li>
              <strong>Sitio web:</strong> https://{BRAND.domain}
            </li>
          </ul>
          <p className="text-sm text-[var(--forest-deep)]/70">
            La actividad se desarrolla actualmente como persona física. {BRAND.name} se
            utiliza como nombre comercial. Cuando se constituya la sociedad mercantil, los datos
            del responsable del tratamiento se actualizarán en consecuencia.
          </p>
          <p>
            {BRAND.name} no ha designado un Delegado de Protección de Datos (DPO) al no estar
            obligada en virtud del artículo 37 del RGPD ni del artículo 34 de la LOPDGDD. No
            obstante, cualquier consulta relativa al tratamiento de datos personales puede
            dirigirse a la dirección de correo indicada.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            2. Datos que recogemos
          </h2>
          <p>Dependiendo del tipo de relación que mantengas con {BRAND.name}, podemos tratar:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Datos de contacto:</strong> nombre y apellidos, correo electrónico,
              teléfono y, en su caso, ciudad o club, facilitados a través del formulario de
              contacto o por WhatsApp.
            </li>
            <li>
              <strong>Datos identificativos del jugador:</strong> edad, sexo, mano dominante,
              hándicap, años de práctica y club, recogidos en el proceso de admisión a la
              valoración.
            </li>
            <li>
              <strong>Datos de salud y rendimiento (categoría especial):</strong> antecedentes
              médicos y deportivos, lesiones previas, movilidad articular, fuerza, equilibrio,
              datos biomecánicos del swing, métricas de TrackMan y resultados de las pruebas con
              VALD ForceFrame y OnForm. Estos datos se recogen exclusivamente durante la
              valoración presencial.
            </li>
            <li>
              <strong>Imágenes y vídeo:</strong> grabaciones del swing y fotografías técnicas
              realizadas durante la sesión, utilizadas para el análisis biomecánico.
            </li>
            <li>
              <strong>Datos de navegación:</strong> información estrictamente técnica necesaria
              para que el sitio web funcione (ver{" "}
              <Link href="/cookies" className="text-[var(--forest)] underline">
                Política de Cookies
              </Link>
              ).
            </li>
          </ul>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            3. Finalidades del tratamiento
          </h2>
          <p>Tratamos tus datos personales con las siguientes finalidades:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Atender y responder a las consultas recibidas a través del sitio web o de WhatsApp.</li>
            <li>
              Gestionar la reserva, programación y seguimiento de las sesiones de valoración
              integral del golfista.
            </li>
            <li>
              Elaborar el informe técnico personalizado con los resultados de las pruebas
              biomecánicas, de fuerza, movilidad, control motor y rendimiento, y compartirlo
              con el jugador y, si así lo autoriza, con su entrenador.
            </li>
            <li>
              Informar al jugador de novedades, recomendaciones y revisiones relacionadas con su
              proceso, siempre que exista una base jurídica que lo legitime.
            </li>
            <li>Cumplir con las obligaciones legales, contables y fiscales aplicables.</li>
          </ul>
          <p>
            No utilizamos tus datos para tomar decisiones automatizadas con efectos jurídicos ni
            para elaborar perfiles comerciales.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            4. Base legal del tratamiento
          </h2>
          <p>Las bases jurídicas que legitiman cada tratamiento son las siguientes:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Consentimiento del interesado</strong> (art. 6.1.a y art. 9.2.a RGPD) para
              el envío de consultas a través del formulario web, la grabación de imágenes y el
              tratamiento de los datos de salud derivados de la valoración.
            </li>
            <li>
              <strong>Ejecución de un contrato</strong> (art. 6.1.b RGPD) cuando contratas los
              servicios de valoración.
            </li>
            <li>
              <strong>Cumplimiento de una obligación legal</strong> (art. 6.1.c RGPD) en materia
              fiscal, contable y de conservación de la documentación.
            </li>
            <li>
              <strong>Interés legítimo</strong> (art. 6.1.f RGPD) para garantizar la seguridad
              del sitio web y prevenir usos fraudulentos.
            </li>
          </ul>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            5. Datos de salud (categoría especial)
          </h2>
          <p>
            Los datos relativos al estado físico del jugador (movilidad, fuerza, equilibrio,
            antecedentes médicos y resultados de las pruebas biomecánicas) tienen la
            consideración de “categorías especiales de datos” conforme al artículo 9 del RGPD.
            {BRAND.name} los trata únicamente con el consentimiento explícito del jugador,
            limitándolos a las personas estrictamente necesarias del equipo (coach, fisioterapeuta
            y traumatólogo) y aplicando medidas técnicas y organizativas adecuadas para
            garantizar su confidencialidad.
          </p>
          <p>
            En el caso de menores de edad, el consentimiento será otorgado por los padres o
            tutores legales y solo se tratarán los datos imprescindibles para realizar la
            valoración.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            6. Plazos de conservación
          </h2>
          <p>
            Conservamos los datos personales únicamente durante el tiempo necesario para cumplir
            con las finalidades para las que fueron recogidos:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Los datos de consultas no convertidas en relación contractual se conservan durante
              un máximo de doce (12) meses desde el último contacto.
            </li>
            <li>
              Los datos clínicos y deportivos de jugadores valorados se conservan durante el
              tiempo en que se mantenga la relación profesional y, posteriormente, durante los
              plazos exigidos por la normativa aplicable en materia de documentación clínica y
              fiscal (con carácter orientativo, cinco años conforme a la Ley 41/2002 y seis años
              conforme al Código de Comercio).
            </li>
            <li>
              Las imágenes y vídeos se conservan únicamente durante el período necesario para el
              análisis y la elaboración del informe, salvo autorización expresa del jugador para
              un uso posterior.
            </li>
          </ul>
          <p>
            Una vez transcurridos dichos plazos, los datos se suprimirán o se anonimizarán de
            forma irreversible.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            7. Destinatarios y cesiones
          </h2>
          <p>
            No se cederán los datos a terceros, salvo obligación legal o autorización expresa del
            interesado. {BRAND.name} puede recurrir a encargados del tratamiento (por ejemplo,
            proveedores de alojamiento web, plataformas de mensajería profesional o servicios de
            facturación electrónica) que tratan los datos por cuenta del responsable y bajo
            estricto contrato conforme al artículo 28 del RGPD. Estos proveedores están
            radicados, con carácter preferente, en el Espacio Económico Europeo. En caso de
            transferencias internacionales, se garantizará la existencia de garantías adecuadas
            mediante cláusulas contractuales tipo aprobadas por la Comisión Europea u otros
            mecanismos previstos en el RGPD.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            8. Derechos de las personas usuarias
          </h2>
          <p>
            Como titular de los datos, puedes ejercer en cualquier momento los siguientes
            derechos reconocidos por el RGPD y la LOPDGDD:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Derecho de acceso a tus datos personales.</li>
            <li>Derecho de rectificación de datos inexactos o incompletos.</li>
            <li>Derecho de supresión (“derecho al olvido”) cuando ya no sean necesarios.</li>
            <li>Derecho de oposición al tratamiento.</li>
            <li>Derecho a la limitación del tratamiento.</li>
            <li>Derecho a la portabilidad de los datos.</li>
            <li>Derecho a retirar el consentimiento prestado en cualquier momento.</li>
            <li>
              Derecho a no ser objeto de decisiones individuales automatizadas, incluida la
              elaboración de perfiles.
            </li>
          </ul>
          <p>
            Puedes ejercer estos derechos enviando un correo electrónico a{" "}
            <a href={`mailto:${BRAND.email}`} className="text-[var(--forest)] underline">
              {BRAND.email}
            </a>
            , adjuntando una copia de tu documento de identidad o medio equivalente que permita
            acreditar tu identidad, e indicando claramente el derecho que deseas ejercer.
            {BRAND.name} responderá tu solicitud en el plazo máximo de un mes, prorrogable hasta
            dos meses adicionales cuando la complejidad o el número de solicitudes lo justifique.
          </p>
          <p>
            Si consideras que tus derechos no han sido debidamente atendidos, tienes derecho a
            presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD), con
            domicilio en C/ Jorge Juan, 6, 28001 Madrid, o a través de su sede electrónica en{" "}
            <a
              href="https://www.aepd.es"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--forest)] underline"
            >
              www.aepd.es
            </a>
            .
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            9. Medidas de seguridad
          </h2>
          <p>
            {BRAND.name} ha adoptado las medidas técnicas y organizativas necesarias para
            garantizar la seguridad, integridad y confidencialidad de los datos personales,
            atendiendo al estado de la técnica, los costes de aplicación y la naturaleza, alcance,
            contexto y fines del tratamiento, así como los riesgos para los derechos y libertades
            de las personas. Entre dichas medidas se incluyen el control de accesos, el cifrado de
            las comunicaciones, las copias de seguridad y la formación interna del equipo en
            materia de protección de datos.
          </p>

          <h2 className="font-display text-2xl font-medium mt-12 mb-4 text-[var(--forest-deep)]">
            10. Cambios en la Política de Privacidad
          </h2>
          <p>
            {BRAND.name} se reserva el derecho a modificar la presente Política de Privacidad para
            adaptarla a novedades legislativas o jurisprudenciales, así como a cambios en sus
            procesos internos. Cualquier modificación se publicará en esta misma página, indicando
            la fecha de última actualización.
          </p>

          <p className="text-sm text-[var(--forest-deep)]/60 italic mt-16">
            Este texto se ha redactado tomando como referencia el RGPD, la LOPDGDD y la LSSI-CE.
            Antes de su publicación definitiva deberá ser revisado por un asesor legal y
            actualizado con los datos de la sociedad mercantil una vez se constituya.
          </p>
        </div>
      </section>

      <PublicFooter />
      <WhatsAppButton />
    </div>
  );
}
